import { useState, useEffect } from "react";
import { OvertimeHeader } from "./OvertimeHeader";
import { OvertimeTable } from "./OvertimeTable";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { FileQuestion } from "lucide-react";
import { OvertimeRequest } from "@/types/supabase-types"


interface OvertimeRecord {
  id: string;
  date: string;
  hours: number;
  status: string;
  justification: string;
  startTime?: string | null;
  endTime?: string | null;
  approverName?: string | null;
  approverComment?: string | null;
  submittedAt: string;
  updatedAt?: string | null;
}

export function OvertimeTabContent({ employeeId }: { employeeId?: string }) {
  const [overtimeHistory, setOvertimeHistory] = useState<OvertimeRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    async function fetchOvertimeData() {
      if (!employeeId) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('overtime_requests')
          .select(`
            id,
            date,
            hours,
            justification,
            status,
            start_time,
            end_time,
            approver_name,
            approver_comment,
            submitted_at,
            updated_at
          `)
          .eq('employee_id', employeeId)
          .order('date', { ascending: false });
        
        if (error) throw error;
        
        setOvertimeHistory(data?.map((record: OvertimeRequest) => ({
          id: record.id,
          date: record.date,
          hours: record.hours,
          justification: record.justification,
          status: record.status,
          startTime: record.start_time,
          endTime: record.end_time,
          approverName: record.approver_name,
          approverComment: record.approver_comment,
          submittedAt: record.submitted_at,
          updatedAt: record.updated_at
        })) || []);
      } catch {
        // Error handled silently
      } finally {
        setIsLoading(false);
      }
    }

    fetchOvertimeData();

    if (!employeeId) return;
    // Setup realtime subscription for this employee's overtime requests
    const channel = supabase
      .channel(`overtime-tab-content-${employeeId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'ShiftSage',
        table: 'overtime_requests',
        filter: `employee_id=eq.${employeeId}`
      }, () => {
        fetchOvertimeData();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [employeeId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (overtimeHistory.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-8">
        <FileQuestion className="h-12 w-12 text-gray-400" />
        <p className="text-gray-500">No overtime records found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <OvertimeHeader employeeId={employeeId} />
      <OvertimeTable records={overtimeHistory} />
    </div>
  );
}
